import React from 'react'
import styled from '@emotion/styled'
import { mediaQueries } from '../../theme'
import { H3 } from '../../components/shared/type'
import useMatchMedia from '../../hooks/use-media'
import { chunkArray } from '../../utils'
import { Slides, useCarousel } from '../shared/carousel'
import Link from '../shared/link'
import BaseNavigation from '../shared/carousel-navigation'
import TemplateList from '../shared/templates/template-list'
import useGalleryNavigation from './hooks/use-gallery-nav'
import useGalleryTheme from './hooks/use-gallery-theme'

const Container = styled.div`
  overflow: hidden;
`

const GroupSlide = styled(TemplateList)`
  min-width: 80%;
  margin-right: ${p => p.theme.space[4]};

  ${mediaQueries.md} {
    min-width: 100%;
    margin: 0;
  }
`

const GroupSlides = styled(Slides)`
  padding: ${p => `0 ${p.theme.space[6]}`};

  ${mediaQueries.md} {
    padding: 0;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.space[8]};
`

const Title = styled(H3)`
  margin: ${p => `0 ${p.theme.space[6]}`};

  sup {
    font-size: ${p => p.theme.fontSizes[1]};
    vertical-align: super;
  }

  ${mediaQueries.md} {
    margin: 0;
  }
`

const Navigation = styled(BaseNavigation)`
  display: none;
  ${mediaQueries.md} {
    display: flex;
  }
`

const GroupShowcase = ({
  group,
  templates,
  templateLoadingStrategy,
  ...rest
}) => {
  const { theme } = useGalleryTheme()
  const isMobile = !useMatchMedia(mediaQueries.md)
  const { createPath } = useGalleryNavigation()
  const firstSubgroup = group.subgroups[0]

  const groupPath = createPath(group.name, firstSubgroup.name)
  const { slidesRef, canScrollPrev, canScrollNext, scrollPrev, scrollNext } =
    useCarousel(group.name, {
      desktop: {
        draggable: false,
      },
    })

  const chunkedTemplates = chunkArray(templates, isMobile ? 1 : 3)

  const navigationProps = {
    canScrollPrev,
    canScrollNext,
    onScrollToPrev: scrollPrev,
    onScrollToNext: scrollNext,
  }

  return (
    <Container {...rest}>
      <Header>
        <Title>
          <Link variant="nav" to={groupPath}>
            {group.name}
          </Link>
          <sup>({group.templateCount})</sup>
        </Title>
        <Navigation {...navigationProps} />
      </Header>

      <GroupSlides ref={slidesRef}>
        {chunkedTemplates.map((chunk, index) => (
          <GroupSlide
            key={index}
            templates={chunk}
            linkedTemplates={templates}
            defaultTheme={theme}
            templateLoadingStrategy={templateLoadingStrategy}
          />
        ))}
      </GroupSlides>
    </Container>
  )
}

export default GroupShowcase
